import type { CognitoUser } from 'amazon-cognito-identity-js'
import { useState } from 'react'
import type { Step } from './LoginSteps'
import {
	completeNewPassword,
	confirmForgotPassword,
	forgotPassword,
	type LoginFlowState,
	signIn,
} from './loginFlowHandlers'

const asFormHandler = (state: LoginFlowState, run: (s: LoginFlowState) => Promise<void>) => (e: React.FormEvent) => {
	e.preventDefault()
	void run(state)
}

function buildLoginFlowState(
	values: Pick<
		LoginFlowState,
		'email' | 'password' | 'newPassword' | 'pendingCognitoUser' | 'resetEmail' | 'resetCode' | 'resetNewPassword'
	>,
	setters: Pick<
		LoginFlowState,
		| 'setStep'
		| 'setError'
		| 'setInfo'
		| 'setBusy'
		| 'setEmail'
		| 'setPassword'
		| 'setPendingCognitoUser'
		| 'setResetCode'
		| 'setResetNewPassword'
	>,
): LoginFlowState {
	return { ...values, ...setters }
}

function buildLoginActions(state: LoginFlowState) {
	return {
		onSignIn: asFormHandler(state, signIn),
		onCompleteNewPassword: asFormHandler(state, completeNewPassword),
		onForgotPassword: asFormHandler(state, forgotPassword),
		onConfirmForgotPassword: asFormHandler(state, confirmForgotPassword),
	}
}

function buildNavigationActions(
	setStep: (step: Step) => void,
	setError: (error: string | null) => void,
	setInfo: (info: string | null) => void,
	setResetEmail: (email: string) => void,
	email: string,
) {
	return {
		goToEmail: () => {
			setError(null)
			setStep('email')
		},
		goToOptions: () => {
			setError(null)
			setStep('options')
		},
		goToForgot: () => {
			setError(null)
			setInfo(null)
			setResetEmail(email)
			setStep('forgot')
		},
		backToForgot: () => {
			setError(null)
			setStep('forgot')
		},
	}
}

export function useLoginFlow() {
	const [step, setStep] = useState<Step>('options')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [info, setInfo] = useState<string | null>(null)

	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [newPassword, setNewPassword] = useState('')
	const [pendingCognitoUser, setPendingCognitoUser] = useState<CognitoUser | null>(null)

	const [resetEmail, setResetEmail] = useState('')
	const [resetCode, setResetCode] = useState('')
	const [resetNewPassword, setResetNewPassword] = useState('')

	const state = buildLoginFlowState(
		{ email, password, newPassword, pendingCognitoUser, resetEmail, resetCode, resetNewPassword },
		{
			setStep,
			setError,
			setInfo,
			setBusy,
			setEmail,
			setPassword,
			setPendingCognitoUser,
			setResetCode,
			setResetNewPassword,
		},
	)

	return {
		step,
		busy,
		error,
		info,
		email,
		setEmail,
		password,
		setPassword,
		newPassword,
		setNewPassword,
		resetEmail,
		setResetEmail,
		resetCode,
		setResetCode,
		resetNewPassword,
		setResetNewPassword,
		...buildNavigationActions(setStep, setError, setInfo, setResetEmail, email),
		...buildLoginActions(state),
	}
}
